<script setup>
import { useI18n } from '@/locales';
import { useToast } from '@base/hooks';
import { nextTick, ref, watch } from 'vue';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const toast = useToast();
const { t } = useI18n();

const scrollContainer = ref(null);
const bottomAnchor = ref(null);

function copyMessage(text) {
  if (!navigator.clipboard) {
    return;
  }

  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success(t('chatMessage.copyMessage'));
    })
    .catch(() => {
      toast.sucess(t('chatMessage.copyError'));
    });
}

watch(
  () => props.messages.length,
  async () => {
    await nextTick();
    bottomAnchor.value?.scrollIntoView({ behavior: 'smooth' });
  }
);
</script>

<template>
  <div
    ref="scrollContainer"
    :class="$style.messages"
  >
    <div
      v-for="message in messages"
      :key="message.id"
      :class="message.sender === 'user' ? $style.userMessage : $style.promptMessage"
      :data-testid="`message${message.id}`"
    >
      <div :class="$style.text">{{ message.text }}</div>
      <div
        v-if="message.sender === 'bot'"
        :class="$style.bottomActions"
        data-testid="messageActions"
      >
        <XButton
          model="plain"
          @click="copyMessage(message.text)"
        >
          <XIcon
            :class="$style.actionIcon"
            icon="copy"
          ></XIcon>
        </XButton>
      </div>
    </div>
    <div
      v-if="loading"
      :class="$style.loadingMessage"
    >
      <XSkeletonLoader
        height="16px"
        width="60%"
        shape="rounded"
      />
      <XSkeletonLoader
        height="16px"
        width="80%"
        shape="rounded"
      />
      <XSkeletonLoader
        height="16px"
        width="70%"
        shape="rounded"
      />
    </div>
    <div ref="bottomAnchor" />
  </div>
</template>

<style lang="scss" module>
.messages {
  display: flex;
  flex-direction: column;
  gap: var(--space-medium);

  .userMessage {
    display: flex;
    align-self: flex-end;
    padding: var(--space-small-xx) var(--space-small-x);
    background-color: var(--color-neutral-50);
    border-radius: var(--border-radius-regular);
  }

  .promptMessage {
    .bottomActions {
      display: flex;
      align-items: center;
      margin-top: var(--space-small-xxx);
      gap: var(--space-small-xx);
    }
  }

  .text {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: inherit;
    font-size: inherit;
    word-break: break-word;
    white-space: pre-wrap;
  }

  .loadingMessage {
    display: flex;
    flex-direction: column;
    gap: var(--space-small-xx);
  }
}

.actionIcon {
  color: var(--color-brand-secondary-400);
}
</style>
